import { Box, Typography } from "@mui/material";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import type { UserAwaitingVerificationDto } from "Infrastructure/Api/Api";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { useUsersAwaitingBankQuery } from "Api/Queries/User/useUsersAwaitingBankQuery";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { appUrl } from "Utils/UrlUtils";
import { useMemo } from "react";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import type { UserDetailPageTab } from "Components/Users/UserDetailPageContent/UserDetailPageContent";
import { UserRolesCell } from "Components/Users/UserListTable/UserRolesCell";
import { getIdentityRoleFilterOptions } from "Utils/TableFilterUtils";
import { useServerTable } from "Hooks/useServerTable";
import { asString, asStringArray } from "Utils/FilterUtils";
import {
  AWAITING_VERIFICATION_SORT_COLUMN_MAP,
  type AwaitingVerificationParams,
} from "Api/Queries/User/awaitingVerificationTypes";

const RESOURCES = Resources.Pages.Content.Users.ToBeVerified;

const createColumns = (
  t: TFunction,
): Array<MRT_ColumnDef<UserAwaitingVerificationDto>> => [
  { accessorKey: "login", header: t(Resources.Common.Login) },
  {
    accessorKey: "firstName",
    header: t(Resources.Common.FirstName),
    accessorFn: ({ firstName }) => firstName || "",
    size: 115,
  },
  {
    accessorKey: "lastName",
    header: t(Resources.Common.LastName),
    accessorFn: ({ lastName }) => lastName || "",
    size: 125,
  },
  {
    accessorKey: "companyName",
    header: t(Resources.Common.CompanyName),
    accessorFn: ({ companyName }) => companyName || "",
  },
  {
    accessorKey: "companyNumber",
    header: t(Resources.Common.CompanyNumber),
    accessorFn: ({ companyNumber }) => companyNumber || "",
    size: 89,
  },
  {
    accessorKey: "roleCode",
    header: t(Resources.Pages.Content.Users.TableHeaders.Role),
    Cell: ({ row }) => (
      <UserRolesCell
        roleCode={row.original.roleCode}
        identityRoles={row.original.identityRoles}
      />
    ),
    filterVariant: "multi-select",
    filterSelectOptions: getIdentityRoleFilterOptions(t),
    size: 100,
    // Multi-value cell — the legacy role column sort would not reflect what is displayed.
    enableSorting: false,
  },
];

export const BankVerificationTable = () => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();

  const { queryParams, tableProps } =
    useServerTable<UserAwaitingVerificationDto>({
      defaultSorting: [{ id: "dateCreated", desc: true }],
      defaultPageSize: 5,
    });

  const apiParams = useMemo(
    (): AwaitingVerificationParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: queryParams.sortBy
        ? (AWAITING_VERIFICATION_SORT_COLUMN_MAP[queryParams.sortBy] ??
          queryParams.sortBy)
        : undefined,
      sortDirection: queryParams.sortDirection,
      login: asString(queryParams.filters.login),
      firstName: asString(queryParams.filters.firstName),
      lastName: asString(queryParams.filters.lastName),
      companyName: asString(queryParams.filters.companyName),
      companyNumber: asString(queryParams.filters.companyNumber),
      roleCodes: asStringArray(queryParams.filters.roleCode),
    }),
    [queryParams],
  );

  const {
    data: awaitingBank,
    isLoading: loadingAwaitingBank,
    isError: isErrorAwaitingBank,
  } = useUsersAwaitingBankQuery({ params: apiParams });

  const columns = useMemo(() => createColumns(t), [t]);

  const table = useMaterialReactTable<UserAwaitingVerificationDto>({
    ...tableProps,
    data: awaitingBank?.items ?? [],
    columns,
    rowCount: awaitingBank?.totalCount ?? 0,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      ...tableProps.state,
      isLoading: loadingAwaitingBank,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => {
      const activeTab: UserDetailPageTab = "bankAccounts";
      const url = `${appUrl("user-detail")}/${row.original.userID}?activeTab=${activeTab}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: { cursor: "pointer" },
      };
    },
  });

  if (isErrorAwaitingBank) {
    return <ErrorText>{t(Resources.Pages.Content.Users.Error)}</ErrorText>;
  }

  return (
    <Box>
      <Typography variant="h4" component="h2" fontSize="1.125rem" mb={2}>
        {t(RESOURCES.Subtitles.BankVerification)}
      </Typography>
      <Box display="grid" alignItems="start">
        <MaterialReactTable table={table} />
      </Box>
    </Box>
  );
};
